import FeatherIcon from 'feather-icons-react';
import dayjs from 'dayjs';
import { Button, Tooltip } from 'antd';
import PatientNameCell from './components/PatientNameCell';
import { InputFromUser } from '../../../components/Input/input';
import { formatTime } from '../../../utils/helpers/dateHelpers';

export const getBaseColumns = (bookingStats, bookingColorMap, dashboardDate) => [
    {
        name: 'Guest Details',
        selector: row => row.patientId?.name || 'N/A',
        sortable: true,
        minWidth: '220px',
        cell: (record) => (
            <PatientNameCell
                record={record}
                bookingStats={bookingStats}
                bookingColorMap={bookingColorMap}
            />
        ),
    },
    {
        name: 'Room Info',
        selector: row => row.currentRoom?.roomId?.roomNo || 'N/A',
        sortable: true,
        cell: (row) => (
            <div className="room-info-cell">
                <div className="d-flex align-items-center gap-1">
                    <FeatherIcon icon="home" size={14} style={{ color: '#64748b' }} />
                    <span className="room-no-text">{row.currentRoom?.roomId?.roomNo || 'N/A'}</span>
                </div>
                <span className="room-type-text">
                    {row.currentRoom?.roomTypeId?.name?.en || row.currentRoom?.roomTypeId?.name || 'N/A'}
                </span>
                <span className="room-type-text" style={{ fontSize: '11px', color: '#64748b' }}>
                    {row.currentRoom?.occupancyModeId?.code || ''}
                </span>
            </div>
        ),
    },
    {
        name: 'Stay Period',
        selector: row => row.admissionDate || 'N/A',
        sortable: true,
        minWidth: '180px',
        cell: (row) => (
            <div className="d-flex flex-column gap-1 py-2">
                <div className="date-cell">
                    <FeatherIcon icon="calendar" size={12} className="date-icon" />
                    <span style={{ fontSize: '0.8rem' }}>
                        <strong>In:</strong> {row.admissionDate ? dayjs(row.admissionDate).format('DD MMM YYYY') : 'N/A'}
                    </span>
                </div>
                <div className="date-cell">
                    <FeatherIcon icon="calendar" size={12} className="date-icon" />
                    <span style={{ fontSize: '0.8rem' }}>
                        <strong>Out:</strong> {row.dischargeDate ? dayjs(row.dischargeDate).format('DD MMM YYYY') : 'N/A'}
                    </span>
                </div>
            </div>
        ),
    },
];

export const getMediDaycareColumns = (handleMediDaycareCheckIn, onStatusUpdate, canUpdate) => [
    {
        name: 'Customer Details',
        selector: row => row.customerName || 'N/A',
        sortable: true,
        minWidth: '180px',
        cell: (record) => (
            <div className="d-flex flex-column">
                <span style={{ fontWeight: 600 }}>{record.customerName || 'N/A'}</span>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{record.phoneNo || 'N/A'}</span>
            </div>
        ),
    },
    {
        name: 'Service',
        selector: row => row.serviceData?.[0]?.name || 'N/A',
        sortable: true,
        minWidth: '150px',
        cell: (record) => (
            <div className="d-flex flex-column">
                {record.serviceData?.map((service, index) => (
                    <span key={index} style={{ fontSize: '0.85rem' }}>
                        {service.name} (x{service.quantity})
                    </span>
                ))}
            </div>
        ),
    },
    {
        name: 'Date & Time',
        selector: row => row.date || 'N/A',
        sortable: true,
        minWidth: '140px',
        cell: (record) => (
            <div className="d-flex flex-column gap-1">
                <div className="date-cell">
                    <FeatherIcon icon="calendar" size={12} className="date-icon" />
                    <span style={{ fontSize: '0.8rem' }}>{record.date || 'N/A'}</span>
                </div>
                <div className="date-cell">
                    <FeatherIcon icon="clock" size={12} className="date-icon" />
                    <span style={{ fontSize: '0.8rem' }}>{formatTime(record.startTime) || 'N/A'}</span>
                </div>
            </div>
        ),
    },
    {
        name: 'Order ID',
        selector: row => row.orderUniqueId || 'N/A',
        sortable: true,
        minWidth: '150px',
    },
    {
        id: 'status',
        name: 'Status',
        selector: row => row.status || 'N/A',
        sortable: true,
        cell: (record) => (
            <>
                {canUpdate ? (
                    <InputFromUser
                        placeholder=""
                        type="singleSelect"
                        name="status"
                        mode=""
                        onChange={newStatus => onStatusUpdate(record.id, newStatus)}
                        options={['Upcoming', 'Completed']}
                        FormGroupClassName="col-xl-12 col-md-12 cursor-pointer m-0"
                        LabelClassName="col-form-label cursor-pointer"
                        value={record.status}
                        disabled={record.status === 'Completed'}
                        label=""
                    />
                ) : (
                    <span
                        style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: record.status === 'Upcoming' ? '#fef3c7' : record.status === 'Completed' ? '#d1fae5' : '#fee2e2',
                            color: record.status === 'Upcoming' ? '#92400e' : record.status === 'Completed' ? '#065f46' : '#991b1b',
                        }}
                    >
                        {record.status}
                    </span>
                )}
            </>
        ),
    },
    {
        name: 'Action',
        id: 'actions',
        cell: (record) => (
            <Tooltip
                title={!record?.patientPlan?.[0]?._id ? "Patient Plan not found" : "Process Check In"}
                placement="top"
            >
                <Button
                    type="text"
                    size="middle"
                    disabled={!record?.patientPlan?.[0]?._id}
                    icon={<FeatherIcon icon="file-text" size={16} />}
                    onClick={() => handleMediDaycareCheckIn(record)}
                    className="action-btn btn-action-view"
                    style={{
                        color: !record?.patientPlan?.[0]?._id ? '#94a3b8' : undefined,
                        opacity: !record?.patientPlan?.[0]?._id ? 0.6 : 1,
                        cursor: !record?.patientPlan?.[0]?._id ? 'not-allowed' : 'pointer'
                    }}
                >
                    CasePaper
                </Button>
            </Tooltip>
        ),
    },
];

export const getCheckInColumns = (baseColumns, handleCheckIn) => [
    ...baseColumns,
    {
        name: 'Action',
        id: 'actions',
        cell: (record) => (
            <Tooltip title={'Process Check In'} placement="top">
                <Button
                    type="text"
                    size="middle"
                    icon={<FeatherIcon icon="log-in" size={16} />}
                    onClick={() => handleCheckIn(record)}
                    disabled={record.status === 'Checked-In' || record.status === 'Completed'}
                    className="action-btn btn-action-view"
                >
                    Check In
                </Button>
            </Tooltip>
        ),
    },
];

export const getInHouseColumns = (baseColumns, dashboardDate, handleInHouseForm, handleCheckOut) => [
    ...baseColumns,
    {
        name: 'Diet Plan',
        selector: row => row.dietPlan?.[dashboardDate] || 'Not Assigned',
        sortable: false,
        cell: (row) => (
            <div className="d-flex flex-column">
                <div className="d-flex align-items-center gap-2">
                    <FeatherIcon
                        icon="coffee"
                        size={14}
                        style={{ color: row.dietPlan?.[dashboardDate] ? '#059669' : '#94a3b8' }}
                    />
                    <span
                        style={{
                            fontWeight: row.dietPlan?.[dashboardDate] ? '600' : '400',
                            color: row.dietPlan?.[dashboardDate] ? '#111827' : '#94a3b8',
                        }}
                    >
                        {row.dietPlan?.[dashboardDate] || 'Not Assigned'}
                    </span>
                </div>
                {row.dietPlanNote && (
                    <span style={{ fontSize: '11px', color: '#666', marginTop: '4px', marginLeft: '22px' }}>
                        {row.dietPlanNote}
                    </span>
                )}
            </div>
        ),
    },
    {
        name: 'Actions',
        id: 'actions',
        cell: (record) => (
            <div className="d-flex align-items-center gap-2">
                <Tooltip
                    title={!record?.patientPlanId ? "Patient Plan not found" : "View Casepaper"}
                    placement="top"
                >
                    <Button
                        type="text"
                        size="middle"
                        disabled={!record?.patientPlanId}
                        icon={<FeatherIcon icon="file-text" size={16} />}
                        onClick={() => handleInHouseForm(record)}
                        className="action-btn btn-action-view"
                        style={{
                            color: !record?.patientPlanId ? '#94a3b8' : undefined,
                            opacity: !record?.patientPlanId ? 0.6 : 1,
                            cursor: !record?.patientPlanId ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Casepaper
                    </Button>
                </Tooltip>

                <Tooltip title={record.status === 'Checked-Out' ? 'Already Checked Out' : 'Process Checkout'} placement="top">
                    <Button
                        type="text"
                        size="middle"
                        icon={<FeatherIcon icon="log-out" size={16} />}
                        onClick={() => handleCheckOut(record)}
                        disabled={record.status === 'Checked-Out' || record.status === 'Completed'}
                        className="action-btn btn-action-checkout"
                    >
                        Check Out
                    </Button>
                </Tooltip>
            </div>
        ),
    },
];
export const getCheckOutColumns = (baseColumns, handleCheckOut) => [
    ...baseColumns,
    {
        name: 'Status',
        selector: row => row.status || 'N/A',
        cell: (row) => (
            <span
                style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: row.status === 'Checked-Out' ? '#dcfce7' : '#fef3c7',
                    color: row.status === 'Checked-Out' ? '#166534' : '#92400e',
                }}
            >
                {row.status}
            </span>
        ),
    },
    {
        name: 'Action',
        id: 'actions',
        cell: (record) => (
            <Tooltip title={record.status === 'Checked-Out' ? 'Already Checked Out' : 'Process Checkout'} placement="top">
                <Button
                    type="text"
                    size="middle"
                    icon={<FeatherIcon icon="log-out" size={16} />}
                    onClick={() => handleCheckOut(record)}
                    disabled={record.status === 'Checked-Out' || record.status === 'Completed'}
                    className="action-btn btn-action-checkout"
                >
                    Check Out
                </Button>
            </Tooltip>
        ),
    },
];
